<template>
  <b-container>
    <b-modal v-model="chartEditVisible" title="edit a chart" @ok="onModalOk" :ok-disabled="aiSubmitting" :ok-title="activeTab === 4 ? (aiSubmitting ? 'updating...' : 'update with AI') : 'OK'" size="xl" modal-class="view-editor-modal">
      <template #title>
        edit a chart
        <modal-help topic="edit-chart" />
      </template>
      <div class="edit-modal-content">
        <b-tabs v-model="activeTab" content-class="tab-body" nav-class="view-editor-tabs" pills>

          <b-tab title="General" active>
            <div class="tab-section">
              <div class="section-heading">Naming</div>
              <div class="field-row">
                <label class="field-label">
                  unique title
                  <b-form-input v-model="uniqueTitle" placeholder="chart unique title" required />
                </label>
              </div>
              <div class="field-row">
                <label class="field-label">
                  title
                  <b-form-input v-model="title" placeholder="chart title" required />
                </label>
              </div>
            </div>
            <div class="tab-section">
              <div class="section-heading">Data Source</div>
              <div class="data-source-display">
                <span class="data-source-spreadsheet">{{ dataSourceLabel }}</span>
                <span v-if="filterByPageRecord" class="data-source-badge">filtered by page record</span>
              </div>
              <label v-if="pageRecordLink" class="checkbox-label page-record-filter">
                <input type="checkbox" v-model="filterByPageRecord" />
                only show records related to the page's {{ pageRecordLink.pageRecordTitle }} record
              </label>
              <p v-if="pageRecordLink" class="section-desc">this page is opened from a link that carries a {{ pageRecordLink.pageRecordTitle }} record. the view keeps records related through {{ pageRecordLink.columnTitle }}.</p>
            </div>
            <div class="tab-section">
              <div class="section-heading">Realtime</div>
              <label class="checkbox-label">
                <input type="checkbox" v-model="realtime" />
                enable realtime updates (data will update automatically when records change)
              </label>
            </div>
          </b-tab>

          <b-tab title="Chart Options">
            <div class="tab-section">
              <div class="field-row">
                <label class="field-label">
                  chart type
                  <b-form-select v-model="chartType" :options="chartTypeOptions" class="mt-1" size="sm" placeholder="select chart type" />
                </label>
              </div>
              <div class="field-row">
                <label class="field-label">
                  label field
                  <p class="field-desc">the spreadsheet field whose values will be used as chart labels (categories)</p>
                  <b-form-select v-model="labelField" :options="spreadSheetColumnOptions" class="mt-1" size="sm" placeholder="select label field" />
                  <b-form-input
                    v-if="labelField"
                    v-model="labelFieldLabel"
                    :placeholder="getColumnTitle(labelField)"
                    class="mt-1 custom-label-input"
                    size="sm"
                  />
                </label>
              </div>
              <div v-for="(vf, idx) in valueFields" :key="idx" class="field-row">
                <label class="field-label">
                  value field{{ valueFields.length > 1 ? ` ${idx + 1}` : '' }}
                  <p class="field-desc">the spreadsheet field whose values will be used as data points</p>
                  <div class="value-field-row">
                    <b-form-select v-model="valueFields[idx]" :options="spreadSheetColumnOptions" class="mt-1 value-field-select" size="sm" placeholder="select value field" />
                    <button v-if="supportsMultipleValueFields && valueFields.length > 1" type="button" class="value-field-btn remove-btn" @click="removeValueField(idx)" title="Remove value field">−</button>
                    <button v-if="supportsMultipleValueFields && idx === valueFields.length - 1" type="button" class="value-field-btn add-btn" @click="addValueField" title="Add value field">+</button>
                  </div>
                  <b-form-input
                    v-if="valueFields[idx]"
                    v-model="valueFieldLabels[idx]"
                    :placeholder="getColumnTitle(valueFields[idx])"
                    class="mt-1 custom-label-input"
                    size="sm"
                  />
                  <label v-if="valueFields[idx] && chartType === 'line'" class="checkbox-label trend-line-toggle">
                    <input type="checkbox" v-model="valueFieldTrendLines[idx]" />
                    show trend line
                  </label>
                </label>
              </div>
              <div class="field-row">
                <label class="field-label">
                  aggregation
                  <p class="field-desc">how values should be combined when multiple records share the same label</p>
                  <b-form-select v-model="aggregation" :options="aggregationOptions" class="mt-1" size="sm" placeholder="select aggregation method" />
                </label>
              </div>
            </div>
          </b-tab>

          <b-tab title="Filtering">
            <div v-if="pageRecordLink" class="tab-section">
              <div class="section-heading">Page record</div>
              <label class="checkbox-label">
                <input type="checkbox" v-model="filterByPageRecord" />
                only show records related to the page's {{ pageRecordLink.pageRecordTitle }} record
              </label>
              <p class="section-desc">this page is opened from a link that carries a {{ pageRecordLink.pageRecordTitle }} record. the view keeps records related through {{ pageRecordLink.columnTitle }}.</p>
            </div>
            <div class="tab-section">
              <label class="checkbox-label">
                <input type="checkbox" v-model="userFiltering" />
                allow users to filter records
              </label>
              <label v-if="userFiltering" class="checkbox-label" style="margin-top: 8px;">
                <input type="checkbox" v-model="restrictUserFiltering" />
                restrict filter fields to fields on this view. if this option is unchecked, users can filter on any field or connected field.
              </label>
            </div>
            <div v-if="userFiltering" class="tab-section">
              <div class="section-heading">Default Filters</div>
              <p class="section-desc">these filters will be displayed by default on the component, and users can change them</p>
              <PreFilterBuilder
                :filters="defaultUserFilters"
                :match-mode="defaultUserFilterMatchMode"
                :spreadsheet="spreadsheet"
                :all-spreadsheets="allSpreadsheets"
                :application-unique-title="applicationUniqueTitle"
                @update:filters="defaultUserFilters = $event"
                @update:match-mode="defaultUserFilterMatchMode = $event"
              />
            </div>
            <div class="tab-section">
              <div class="section-heading">Prefilters</div>
              <p class="section-desc">these filters will always be applied to the record set and will not be displayed on the view</p>
              <PreFilterBuilder
                :filters="preFilters"
                :match-mode="preFilterMatchMode"
                :spreadsheet="spreadsheet"
                :all-spreadsheets="allSpreadsheets"
                :application-unique-title="applicationUniqueTitle"
                @update:filters="preFilters = $event"
                @update:match-mode="preFilterMatchMode = $event"
              />
            </div>
            <FilterButtonEditor
              :buttons="filterButtons"
              :allow-multiple="allowMultipleFilterButtons"
              :spreadsheet="spreadsheet"
              :all-spreadsheets="allSpreadsheets"
              :application-unique-title="applicationUniqueTitle"
              @update:buttons="filterButtons = $event"
              @update:allow-multiple="allowMultipleFilterButtons = $event"
            />
          </b-tab>

          <b-tab title="Sorting">
            <div class="tab-section">
              <div class="section-heading">Sort Order</div>
              <p class="section-desc">records used by this chart are ordered by these columns, from highest precedence to lowest. categories follow that record order.</p>
              <SortBuilder
                :sorts="sorts"
                :spreadsheet="spreadsheet"
                @update:sorts="sorts = $event"
              />
            </div>
          </b-tab>

          <b-tab title="AI">
            <ViewAiPanel v-model="aiPrompt" :error="aiError || ''" :submitting="aiSubmitting" />
          </b-tab>

        </b-tabs>
      </div>
    </b-modal>
  </b-container>
</template>

<script>

  import PreFilterBuilder from './lib/PreFilterBuilder.vue'
  import FilterButtonEditor from './lib/FilterButtonEditor.vue'
  import SortBuilder from './lib/SortBuilder.vue'
  import { submitViewAi } from '../lib/view-ai.js'
  import { pageRecordRelationship } from '../lib/page-record-link.js'
  import ViewAiPanel from './ViewAiPanel.vue'

  export default {
    name: `chartEdit`,
    data () {

      return {
        chartEditVisible: false,
        activeTab: 0,
        aiPrompt: ``,
        aiError: ``,
        aiSubmitting: false,
        aiSavedUniqueTitle: ``,
        uniqueTitle: ``,
        title: ``,
        spreadSheetColumnOptions: [],
        spreadSheetUniqueTitle: ``,
        spreadsheet: null,
        allSpreadsheets: [],
        chartType: `bar`,
        labelField: ``,
        labelFieldLabel: ``,
        valueFields: [``],
        valueFieldLabels: [``],
        valueFieldTrendLines: [false],
        aggregation: `sum`,
        chartTypeOptions: [
          { value: `bar`, text: `Bar` },
          { value: `line`, text: `Line` },
          { value: `pie`, text: `Pie` },
          { value: `doughnut`, text: `Doughnut` },
          { value: `radar`, text: `Radar` },
          { value: `polarArea`, text: `Polar Area` }
        ],
        aggregationOptions: [
          { value: `sum`, text: `Sum` },
          { value: `count`, text: `Count` },
          { value: `average`, text: `Average` },
          { value: `min`, text: `Min` },
          { value: `max`, text: `Max` }
        ],
        realtime: false,
        userFiltering: false,
        restrictUserFiltering: false,
        defaultUserFilters: [],
        defaultUserFilterMatchMode: 'all',
        preFilters: [],
        preFilterMatchMode: 'all',
        filterButtons: [],
        allowMultipleFilterButtons: false,
        sorts: [],
        parentDesigner: null,
        filterByPageRecord: false,
        pageRecordSpreadSheetUniqueTitle: '',
        relationshipColumnTitle: '',
        applicationUniqueTitle: ''
      }
    },
    components: {
      PreFilterBuilder,
      FilterButtonEditor,
      SortBuilder,
      ViewAiPanel
    },
    computed: {
      supportsMultipleValueFields () {
        return this.chartType === 'bar' || this.chartType === 'line'
      },
      pageRecordLink () {
        return pageRecordRelationship(this.allSpreadsheets, this.spreadSheetUniqueTitle, this.pageRecordSpreadSheetUniqueTitle)
      },
      dataSourceLabel () {
        const title = (this.spreadsheet && this.spreadsheet.title) || this.spreadSheetUniqueTitle || 'unknown'
        if (!this.filterByPageRecord) return title

        const link = this.pageRecordLink
        const pageRecordTitle = link ? link.pageRecordTitle : this.pageRecordSpreadSheetUniqueTitle
        const columnTitle = link ? link.columnTitle : this.relationshipColumnTitle
        let label = title
        if (pageRecordTitle) label += ` — related to page's ${pageRecordTitle} record`
        if (columnTitle) label += ` via ${columnTitle}`
        return label
      }
    },
    watch: {
      chartType (newType) {
        if (newType !== 'bar' && newType !== 'line') {
          if (this.valueFields.length > 1) {
            this.valueFields = [this.valueFields[0]]
            this.valueFieldLabels = [this.valueFieldLabels[0] || '']
            this.valueFieldTrendLines = [this.valueFieldTrendLines[0] || false]
          }
        }
      }
    },
    methods: {
      addValueField () {
        this.valueFields.push(``)
        this.valueFieldLabels.push(``)
        this.valueFieldTrendLines.push(false)
      },
      removeValueField (idx) {
        if (this.valueFields.length > 1) {
          this.valueFields.splice(idx, 1)
          this.valueFieldLabels.splice(idx, 1)
          this.valueFieldTrendLines.splice(idx, 1)
        }
      },
      getColumnTitle (uniqueTitle) {
        if (!uniqueTitle) return ''
        const systemTitles = { id: 'ID', createdAt: 'Created At', updatedAt: 'Updated At' }
        if (systemTitles[uniqueTitle]) return systemTitles[uniqueTitle]
        if (!this.spreadsheet || !this.spreadsheet.columns) return ''
        const col = this.spreadsheet.columns.find(c => c.uniqueTitle === uniqueTitle)
        return col ? col.title : uniqueTitle
      },
      async getSpreadSheet () {

        console.log(`getSpreadSheet! ${this.spreadSheetUniqueTitle}`)

        const spreadsheet = await this.$http.get(`${VUE_APP_PROXY_SERVER_URL}/spreadsheets/${this.spreadSheetUniqueTitle}`, {
          headers: { 'application-unique-title': this.applicationUniqueTitle }
        })

        this.spreadsheet = spreadsheet.data

        if (!this.spreadsheet.columns) {

          return
        }

        const systemFields = [
          { value: 'id', text: 'ID (system)' },
          { value: 'createdAt', text: 'Created At (system)' },
          { value: 'updatedAt', text: 'Updated At (system)' }
        ]

        this.spreadSheetColumnOptions = [
          ...systemFields,
          ...this.spreadsheet.columns.map((column) => {
            return {
              value: column.uniqueTitle,
              text: column.title
            }
          })
        ]
      },
      async showEditChartComponent (spreadSheetUniqueTitle) {

        this.aiPrompt = ''
        this.aiError = ''
        this.aiSubmitting = false
        this.aiSavedUniqueTitle = this.uniqueTitle

        this.activeTab = 0
        this.spreadSheetUniqueTitle = spreadSheetUniqueTitle

        await this.getSpreadSheet()

        this.chartEditVisible = true
      },
      async onModalOk (event) {

        const handled = await submitViewAi(this, event, { visibleKey: 'chartEditVisible', tabIndex: 4 })

        if (!handled) return this.save()
      },
      async save () {

        console.log(`save chart component!`)

        const labelDefault = this.getColumnTitle(this.labelField)
        const resolvedLabelFieldLabel = (this.labelFieldLabel && this.labelFieldLabel !== labelDefault)
          ? this.labelFieldLabel
          : undefined

        const activeValueFields = this.valueFields.filter(vf => vf !== '')
        const resolvedValueFieldLabels = this.valueFieldLabels
          .slice(0, this.valueFields.length)
          .map((label, idx) => {
            const fieldUT = this.valueFields[idx]
            if (!fieldUT) return undefined
            const defaultTitle = this.getColumnTitle(fieldUT)
            return (label && label !== defaultTitle) ? label : undefined
          })

        const hasAnyCustomValueLabel = resolvedValueFieldLabels.some(l => l !== undefined)

        const resolvedTrendLines = this.valueFieldTrendLines
          .slice(0, this.valueFields.length)
          .filter((_, idx) => this.valueFields[idx] !== '')
        const hasAnyTrendLine = resolvedTrendLines.some(t => t === true)

        this.parentDesigner.saveActiveComponent({
          uniqueTitle: this.uniqueTitle,
          title: this.title,
          spreadSheetUniqueTitle: this.spreadSheetUniqueTitle,
          chartType: this.chartType,
          labelField: this.labelField,
          labelFieldLabel: resolvedLabelFieldLabel,
          valueField: this.valueFields[0] || '',
          valueFields: activeValueFields,
          valueFieldLabels: hasAnyCustomValueLabel
            ? resolvedValueFieldLabels.filter((_, idx) => this.valueFields[idx] !== '')
            : undefined,
          valueFieldTrendLines: hasAnyTrendLine ? resolvedTrendLines : undefined,
          aggregation: this.aggregation,
          userFiltering: this.userFiltering,
          restrictUserFiltering: this.restrictUserFiltering,
          defaultUserFilters: this.defaultUserFilters,
          defaultUserFilterMatchMode: this.defaultUserFilterMatchMode,
          preFilters: this.preFilters,
          preFilterMatchMode: this.preFilterMatchMode,
          filterButtons: this.filterButtons,
          allowMultipleFilterButtons: !!this.allowMultipleFilterButtons,
          sorts: this.sorts,
          realtime: this.realtime,
          filterByPageRecord: !!this.filterByPageRecord
        })
      }
    }
  }
</script>

<style>
.field-desc {
  color: #6a6a67;
  font-size: 0.85em;
  margin-bottom: 4px;
  margin-top: 2px;
  line-height: 1.4;
}
.value-field-row {
  display: flex;
  align-items: center;
  gap: 6px;
}
.value-field-select {
  flex: 1;
}
.value-field-btn {
  width: 28px;
  height: 28px;
  border: 1px solid #555;
  border-radius: 4px;
  background: #2a2a2a;
  color: #ccc;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0;
  margin-top: 4px;
}
.value-field-btn:hover {
  background: #3a3a3a;
  color: #fff;
}
.value-field-btn.remove-btn:hover {
  border-color: #e74c3c;
  color: #e74c3c;
}
.value-field-btn.add-btn:hover {
  border-color: #2ecc71;
  color: #2ecc71;
}
.custom-label-input {
  font-size: 0.85em;
  opacity: 0.85;
}
.custom-label-input::placeholder {
  font-style: italic;
  opacity: 0.5;
}
.trend-line-toggle {
  margin-top: 6px;
  font-size: 0.85em;
  opacity: 0.85;
}
</style>
